import { z } from 'zod';
import { BackendError } from '@/api/adapter/client';
import {
  operationRefSchema,
  submitProjectCommand,
} from '@/api/adapter/projectCommands';
import { getOperation } from '@/api/adapter/onboarding';
import {
  projectCommandSchema,
  commandScope,
  validateProjectCommand,
  type ProjectCommand,
} from '@/types/project-command';
import { useAuthStore } from '@/stores/authStore';
import { operationResult } from './workerRegistration';

const operationSchema = operationRefSchema.extend({
  errorCode: z.string().nullable(),
  receipt: z
    .object({
      operationId: operationRefSchema.shape.operationId,
      origin: z.string().regex(/^0x[0-9a-f]{64}$/),
      outcome: z.discriminatedUnion('type', [
        z.object({ type: z.literal('Success') }),
        z.object({ type: z.literal('Failed'), code: z.string() }),
      ]),
      createdEntityId: operationRefSchema.shape.operationId.nullable(),
    })
    .nullable(),
});
const pendingSchema = z.object({
  key: z.string().regex(/^0x[0-9a-f]{32}$/),
  operationId: operationRefSchema.shape.operationId.optional(),
  command: projectCommandSchema,
});
const storageKey = (principal: string, projectId?: string) =>
  'abako-project-command:' + principal + ':' + (projectId ?? 'new');
export function pendingProjectCommand(principal: string, projectId?: string) {
  const raw = sessionStorage.getItem(storageKey(principal, projectId));
  if (!raw) return null;
  try {
    const value = pendingSchema.parse(JSON.parse(raw));
    if (commandScope(value.command) !== projectId)
      throw new Error('Wrong project');
    return value;
  } catch {
    throw new Error(
      'The saved project request cannot be read. Check server state before resubmitting.'
    );
  }
}
export function clearProjectCommand(principal: string, projectId?: string) {
  sessionStorage.removeItem(storageKey(principal, projectId));
}
export async function completeProjectCommand(
  principal: string,
  input: ProjectCommand | null,
  projectId: string | undefined,
  onStatus: (message: string) => void,
  signal: AbortSignal
) {
  const assertOwner = () => {
    if (
      signal.aborted ||
      useAuthStore.getState().session?.principalId !== principal
    )
      throw new Error('Project request paused. Log in to resume it.');
  };
  assertOwner();
  let pending = pendingProjectCommand(principal, projectId);
  if (!pending) {
    const command = projectCommandSchema.parse(input);
    validateProjectCommand(command);
    if (commandScope(command) !== projectId)
      throw new Error('Project request does not match this page.');
    pending = { key: '0x' + crypto.randomUUID().replace(/-/g, ''), command };
    sessionStorage.setItem(
      storageKey(principal, projectId),
      JSON.stringify(pending)
    );
  }
  if (!pending.operationId) {
    try {
      const reference = await submitProjectCommand(
        pending.command,
        pending.key,
        signal
      );
      // Persist the reference even if the page/account changed during the request.
      pending.operationId = reference.operationId;
      sessionStorage.setItem(
        storageKey(principal, projectId),
        JSON.stringify(pending)
      );
    } catch (error) {
      // These responses cannot have accepted a command. Uncertain responses keep the exact key/body.
      if (
        error instanceof BackendError &&
        [400, 413, 422].includes(error.statusCode)
      )
        clearProjectCommand(principal, projectId);
      throw error;
    }
  }
  const deadline = Date.now() + 60000;
  while (Date.now() < deadline) {
    assertOwner();
    const operation = await getOperation(pending.operationId);
    assertOwner();
    const parsed = operationSchema.safeParse(operation);
    const receipt = parsed.success ? parsed.data.receipt : null;
    // Operator ramp commands sign with the system wallet. This authenticated
    // operation read is still restricted by the adapter to its owning principal.
    const systemOrigin =
      useAuthStore.getState().session?.isAdmin &&
      (pending.command.kind === 'bramp-confirm' ||
        pending.command.kind === 'bramp-cancel');
    if (
      !parsed.success ||
      operation.operationId !== pending.operationId ||
      (receipt &&
        (receipt.operationId !== operation.operationId ||
          (!systemOrigin &&
            receipt.origin !== useAuthStore.getState().session?.accountId)))
    )
      throw new Error(
        'Invalid operation status. Check the same request again.'
      );
    try {
      if (
        operationResult(
          operation,
          pending.command.kind === 'skill-request'
            ? 'Skill request'
            : 'Project request'
        ) === 'success'
      ) {
        if (
          [
            'create',
            'proposal-create',
            'completion-submit',
            'dispute-open',
            'bramp-deposit',
            'bramp-withdrawal',
            'skill-request',
          ].includes(pending.command.kind) &&
          !operationRefSchema.shape.operationId.safeParse(
            operation.receipt?.createdEntityId
          ).success
        )
          throw new Error(
            'Creation has no valid entity ID. Check the same request again.'
          );
        if (
          (pending.command.kind === 'bramp-confirm' ||
            pending.command.kind === 'bramp-cancel') &&
          operation.receipt?.createdEntityId !== pending.command.requestId
        )
          throw new Error(
            'The receipt does not match this ramp request. Check the same operation again.'
          );
        return operation;
      }
    } catch (error) {
      if (
        operation.status === 'Rejected' ||
        operation.status === 'Expired' ||
        (operation.status === 'Finalized' &&
          operation.receipt?.outcome.type !== 'Success' &&
          operation.receipt)
      )
        clearProjectCommand(principal, projectId);
      throw error;
    }
    onStatus(
      operation.status === 'OutcomeUnknown'
        ? 'Outcome unknown. Checking the same operation…'
        : 'Request accepted. Waiting for confirmation…'
    );
    await new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', finish);
        resolve();
      };
      const timer = setTimeout(finish, 2000);
      signal.addEventListener('abort', finish, { once: true });
    });
  }
  onStatus(
    'Still pending. Continue checking this saved request; no new request will be sent.'
  );
  return null;
}
